import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Scale, Sparkles, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PROFILE, SOCIALS } from '../mock/data';
import SocialIcon from './SocialIcon';

const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer id="contact" className="relative pt-24 pb-10 paper-texture" style={{ background: 'linear-gradient(180deg, #0a0f1e 0%, #070a14 100%)' }}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12"
        >
          <div className="lg:col-span-5">
            <div className="label-small mb-4">{t.footer.label}</div>
            <h2 className="font-heading text-4xl md:text-5xl text-[#F0EDE8] leading-[1.05]">
              {t.footer.heading}
            </h2>
            <div className="gold-rule mt-6 mb-8" />
            <p className="font-heading italic text-[#DCCFAD] text-lg">
              {PROFILE.tagline}
            </p>
            <Link to="/contact" className="btn-gold mt-8" data-testid="footer-contact-cta">
              <Mail size={14} />
              {t.footer.contactCta}
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-8">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-3">
                  {t.footer.emailLabel}
                </div>
                <a href={`mailto:${PROFILE.email}`} className="group flex items-center gap-3 text-[#F0EDE8] hover:text-[#C4A35A] transition-colors" data-testid="footer-email-link">
                  <Mail size={18} className="text-[#C4A35A]" />
                  <span className="font-heading text-lg md:text-xl tracking-wide">{PROFILE.email}</span>
                </a>
              </div>

              <div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-2">
                  <Phone size={12} />
                  {t.footer.phoneLabel}
                </div>
                <a href={`tel:${PROFILE.phone.replace(/[^0-9+]/g, '')}`} className="text-[#F0EDE8] hover:text-[#C4A35A] transition-colors font-heading text-lg tracking-wide" data-testid="footer-phone-link">
                  {PROFILE.phone}
                </a>
              </div>

              <div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-2">
                  <MapPin size={12} />
                  {t.footer.locationLabel}
                </div>
                <div className="text-[#F0EDE8] font-heading text-lg tracking-wide">
                  {PROFILE.location}
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-2">
                  <Scale size={12} />
                  {t.footer.roleLabel}
                </div>
                <div className="text-[#F0EDE8] text-[15px]">{t.footer.roleValue}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-2">
                  <Sparkles size={12} />
                  {t.footer.barLabel}
                </div>
                <Link to="/retainr" className="group inline-flex items-center gap-2 text-[#F0EDE8] hover:text-[#C4A35A] transition-colors text-[15px]" data-testid="footer-retainr-link">
                  {t.footer.barValue}
                  <ArrowUpRight size={13} className="text-[#8a7a54] group-hover:text-[#C4A35A]" />
                </Link>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#8a7a54] mb-3">
                  {t.footer.followLabel}
                </div>
                <div className="flex gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.id}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${s.label} ${s.handle}`}
                      data-testid={`footer-social-${s.id}`}
                      className="w-10 h-10 flex items-center justify-center border border-[rgba(196,163,90,0.3)] text-[#DCCFAD] hover:text-[#0A0E1A] hover:bg-[#C4A35A] hover:border-[#C4A35A] transition-colors"
                    >
                      <SocialIcon id={s.id} size={16} />
                    </a>
                  ))}
                </div>
              </div>
              <div className="text-[#DCCFAD] text-[14px] leading-relaxed pt-2 border-t border-[rgba(196,163,90,0.15)]">
                Kenneth Christopher &ldquo;KC&rdquo; Redding
                <br />
                <span className="text-[#8a7a54] text-sm">{PROFILE.credentialLine}</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="gold-rule-full my-12" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-heading text-sm text-[#8a7a54]">
            &copy; {year} Kenneth Christopher Redding. {t.footer.rights}.
          </div>
          <div className="font-heading italic text-sm text-[#C4A35A] tracking-wide">
            {PROFILE.tagline}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
